"use client";

import { useRef } from "react";
import Image from "next/image";

import TeamFounder from "./TeamFounder";
import { advisorMembers, teamMembers, type TeamMember } from "@/data/TeamData";
import team_bg from "@/assets/img/update/bg/bg-gradient1-1.png";
import { useLanguage } from "@/utils/LanguageContext";
import { useParallaxTilt, useScrollFade } from "@/lib/MotionProvider";
import "@/styles/sections.css";
import GetInvolvedButton from "@/component/common/GetInvolvedButton";

const Team = () => {
  const { t } = useLanguage();
  const sections = [
    {
      id: "team-members",
      title: "Team",
      members: teamMembers,
    },
    {
      id: "team-advisors",
      title: "Advisors",
      members: advisorMembers,
      featured: true,
    },
  ];

  return (
    <section
      className="pt-130 pb-140 overflow-hidden position-relative z-index-common home-anchor-target"
      id="team"
      aria-labelledby="team-title"
    >
      <div className="bg-gradient-3">
        <Image src={team_bg} alt="" loading="lazy" />
      </div>
      <h2 id="team-title" className="visually-hidden">{t("Team")}</h2>
      <TeamFounder />

      <div className="container">
        <div className="team-directory-nav">
          {sections.map((section) => (
            <a
              key={section.id}
              className={`team-directory-nav-card${section.featured ? " is-featured" : ""}`}
              href={`#${section.id}`}
            >
              <span className="team-directory-nav-label">{t(section.title)}</span>
              <span className="team-directory-nav-count">{section.members.length}</span>
            </a>
          ))}
        </div>

        <div className="team-directory-stack">
          {sections.map((section) => (
            <section
              key={section.id}
              className={`team-directory-section home-anchor-subtarget${section.featured ? " team-directory-section--featured" : ""}`}
              id={section.id}
              aria-labelledby={`${section.id}-title`}
            >
              <div className="team-directory-section-header">
                <h2 className="title style2 team-directory-title" id={`${section.id}-title`}>{t(section.title)}</h2>
                <span className="team-directory-section-count" aria-hidden="true">
                  {String(section.members.length).padStart(2, "0")}
                </span>
              </div>

              <ul className="team-directory-grid">
                {section.members.map((member) => (
                  <TeamCard key={member.id} member={member} />
                ))}
              </ul>
            </section>
          ))}
        </div>
        <GetInvolvedButton />
      </div>
    </section>
  );
};

const TeamCard = ({ member }: { member: TeamMember }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  useScrollFade(ref);
  useParallaxTilt(ref);

  return (
    <li className="team-directory-item">
      <div ref={ref} className="team-card team-directory-card">
        <div className="team-card_img">
          <Image src={member.thumb} alt={member.name} loading="lazy" />
          <div className="social-btn">
            <a
              href={member.linkedin ?? "https://www.linkedin.com/"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <i className="fab fa-linkedin"></i>
            </a>
          </div>
        </div>
        <div className="team-card_content">
          <h3 className="team-card_title">
            {member.linkedin ? (
              <a href={member.linkedin} target="_blank" rel="noopener noreferrer">
                {member.name}
              </a>
            ) : (
              member.name
            )}
          </h3>
          <p className="team-card-desig">{member.designasion || "Advisor"}</p>
          {member.bio && <p className="team-card-bio">{t(member.bio)}</p>}
        </div>
      </div>
    </li>
  );
};

export default Team;
